/**
 * PlanObject derived geometry (pure): world pose (incl. wall-hosted
 * devices), run lengths, areas, footprints, containment, and quantity
 * aggregation for scope bindings. No DOM, no persistence.
 */
import type { PlanAnalysis } from "./analysis";
import { regionAt } from "./analysis";
import type { PlanModel, PlanObject, Point } from "./model";
import { activeObjects, nodeOf } from "./model";
import { PU_PER_FOOT, puToFeetX100, roundDiv } from "./units";
import { direction, distance, leftNormal, pointAlong, roundPoint } from "./vector";

export interface ObjectPose {
  /** Center of the footprint (point objects) / midpoint (runs). */
  center: Point;
  /** Rotation in radians (0 = width along +x), derived for hosted objects. */
  rotationRad: number;
}

/** World pose. Hosted objects derive position/rotation from their wall. */
export function objectPose(model: PlanModel, o: PlanObject): ObjectPose {
  if (o.hostWallId) {
    const wall = model.walls.get(o.hostWallId);
    if (wall && wall.archivedAt === null) {
      const a = nodeOf(model, wall.startNodeId);
      const b = nodeOf(model, wall.endNodeId);
      const len = distance(a, b);
      const offset = Math.max(0, Math.min(len, o.hostOffset ?? 0));
      const at = pointAlong(a, b, offset);
      const n = leftNormal(a, b);
      const side = o.hostSide ?? 1;
      const out = wall.thickness / 2 + o.depth / 2;
      const d = direction(a, b);
      return {
        center: roundPoint({ x: at.x + n.x * out * side, y: at.y + n.y * out * side }),
        rotationRad: Math.atan2(d.y, d.x) + (side === 1 ? 0 : Math.PI),
      };
    }
  }
  if (o.geometryType !== "point" && o.endX !== null && o.endY !== null) {
    const d = direction({ x: o.x, y: o.y }, { x: o.endX, y: o.endY });
    return {
      center: roundPoint({ x: (o.x + o.endX) / 2, y: (o.y + o.endY) / 2 }),
      rotationRad: Math.atan2(d.y, d.x),
    };
  }
  return { center: { x: o.x, y: o.y }, rotationRad: (o.rotationX10 / 10) * (Math.PI / 180) };
}

/** Run length in pu (linear/area objects). */
export function objectRunLength(o: PlanObject): number {
  if (o.geometryType === "point" || o.endX === null || o.endY === null) return 0;
  return Math.round(distance({ x: o.x, y: o.y }, { x: o.endX, y: o.endY }));
}

/** Footprint corners (CCW) in world coordinates. */
export function objectFootprint(model: PlanModel, o: PlanObject): Point[] {
  const pose = objectPose(model, o);
  const halfW = (o.geometryType === "point" ? o.width : objectRunLength(o)) / 2;
  const halfD = o.depth / 2;
  const cos = Math.cos(pose.rotationRad);
  const sin = Math.sin(pose.rotationRad);
  const corner = (dx: number, dy: number): Point =>
    roundPoint({ x: pose.center.x + dx * cos - dy * sin, y: pose.center.y + dx * sin + dy * cos });
  return [corner(-halfW, -halfD), corner(halfW, -halfD), corner(halfW, halfD), corner(-halfW, halfD)];
}

/** Area object sqft × 100 (length × depth, exact half-up). */
export function objectAreaSqftX100(o: PlanObject): number {
  const len = objectRunLength(o);
  return roundDiv(len * o.depth * 100, PU_PER_FOOT * PU_PER_FOOT);
}

/**
 * How much wall an object occupies, in plan units. A run measures end to
 * end; a MODULE measures its own width — a 30" sink base is two and a half
 * feet of cabinetry whether it was drawn as a run or placed as a box
 * (spec §61G), which is what lets modules and runs share one estimate.
 */
export function objectLinearExtentPu(o: PlanObject): number {
  return o.geometryType === "point" ? o.width : objectRunLength(o);
}

/** Linear object LF × 100. */
export function objectLengthLfX100(o: PlanObject): number {
  return puToFeetX100(objectLinearExtentPu(o));
}

/** The Room an object belongs to: explicit pin, else region containment of its anchor. */
export function objectRoomId(model: PlanModel, analysis: PlanAnalysis, o: PlanObject): string | null {
  if (o.roomId) return o.roomId;
  const region = regionAt(analysis, objectPose(model, o).center);
  if (!region || region.roomIds.length !== 1) return null;
  return region.roomIds[0];
}

/** True when the pinned room disagrees with geometric containment (panel warning). */
export function objectPinConflicts(model: PlanModel, analysis: PlanAnalysis, o: PlanObject): boolean {
  if (!o.roomId) return false;
  const region = regionAt(analysis, objectPose(model, o).center);
  return !!region && region.roomIds.length === 1 && region.roomIds[0] !== o.roomId;
}

export type ObjectQuantityRule = "count" | "run-length" | "area";

/**
 * Aggregate quantity (×100) for a feature over its bound objects:
 * count → objects × 100; run-length → Σ LF × 100; area → Σ sqft × 100.
 * Null when no objects are bound (feature quantity becomes unknown).
 */
export function aggregateObjectQuantityX100(objects: PlanObject[], rule: ObjectQuantityRule): number | null {
  if (objects.length === 0) return null;
  switch (rule) {
    case "count":
      return objects.length * 100;
    case "run-length":
      return objects.reduce((sum, o) => sum + objectLengthLfX100(o), 0);
    case "area":
      return objects.reduce((sum, o) => sum + objectAreaSqftX100(o), 0);
  }
}

export function objectsBoundToFeature(model: PlanModel, featureId: string): PlanObject[] {
  return activeObjects(model).filter((o) => o.featureId === featureId);
}

/** Soft collision hint: does the footprint center sit inside a physical wall band? */
export function objectOverlapsWall(model: PlanModel, o: PlanObject, tolerance = 0): boolean {
  const pose = objectPose(model, o);
  for (const w of model.walls.values()) {
    if (w.archivedAt !== null || w.kind === "boundary" || w.thickness === 0) continue;
    const a = nodeOf(model, w.startNodeId);
    const b = nodeOf(model, w.endNodeId);
    const ab = { x: b.x - a.x, y: b.y - a.y };
    const l2 = ab.x * ab.x + ab.y * ab.y;
    if (l2 === 0) continue;
    const t = ((pose.center.x - a.x) * ab.x + (pose.center.y - a.y) * ab.y) / l2;
    if (t < 0 || t > 1) continue;
    const px = a.x + ab.x * t;
    const py = a.y + ab.y * t;
    if (Math.hypot(pose.center.x - px, pose.center.y - py) < w.thickness / 2 + tolerance) return true;
  }
  return false;
}

export interface PlacementSnap {
  center: Point;
  rotationX10: number;
  hostWallId: string | null;
  hostOffset: number | null;
  hostSide: 1 | -1 | null;
  label: string;
}

/**
 * Placement snapping for point objects: back-to-wall (footprint back flush
 * with the nearest physical wall face, facing away), else free with the
 * cursor position. `wallHosted` objects return host fields instead.
 */
export function snapObjectPlacement(
  model: PlanModel,
  cursor: Point,
  size: { width: number; depth: number },
  options: { tolerancePu: number; wallHosted: boolean; enabled: boolean },
): PlacementSnap {
  const free: PlacementSnap = { center: roundPoint(cursor), rotationX10: 0, hostWallId: null, hostOffset: null, hostSide: null, label: "" };
  if (!options.enabled) return free;
  let best: { wallId: string; point: Point; t: number; side: 1 | -1; dist: number; a: Point; b: Point; thickness: number } | null = null;
  for (const w of model.walls.values()) {
    if (w.archivedAt !== null || w.kind === "boundary") continue;
    const a = nodeOf(model, w.startNodeId);
    const b = nodeOf(model, w.endNodeId);
    const ab = { x: b.x - a.x, y: b.y - a.y };
    const l2 = ab.x * ab.x + ab.y * ab.y;
    if (l2 === 0) continue;
    const t = Math.max(0, Math.min(1, ((cursor.x - a.x) * ab.x + (cursor.y - a.y) * ab.y) / l2));
    const px = a.x + ab.x * t;
    const py = a.y + ab.y * t;
    const dist = Math.hypot(cursor.x - px, cursor.y - py);
    if (dist <= options.tolerancePu + w.thickness / 2 + size.depth && (!best || dist < best.dist)) {
      const n = leftNormal(a, b);
      const side: 1 | -1 = (cursor.x - px) * n.x + (cursor.y - py) * n.y >= 0 ? 1 : -1;
      best = { wallId: w.id, point: { x: px, y: py }, t, side, dist, a, b, thickness: w.thickness };
    }
  }
  if (!best) return free;
  const n = leftNormal(best.a, best.b);
  const d = direction(best.a, best.b);
  const out = best.thickness / 2 + size.depth / 2;
  const center = roundPoint({ x: best.point.x + n.x * out * best.side, y: best.point.y + n.y * out * best.side });
  const angleRad = Math.atan2(d.y, d.x) + (best.side === 1 ? 0 : Math.PI);
  const rotationX10 = Math.round(((angleRad * 180) / Math.PI) * 10);
  if (options.wallHosted) {
    return {
      center,
      rotationX10,
      hostWallId: best.wallId,
      hostOffset: Math.round(distance(best.a, best.point)),
      hostSide: best.side,
      label: "⊥ Wall (hosted)",
    };
  }
  return { center, rotationX10, hostWallId: null, hostOffset: null, hostSide: null, label: "⊥ Back to wall" };
}

/**
 * Run drawing snapping (cabinet runs): snap the run line onto the nearest
 * physical wall FACE (offset by depth/2 so the run back touches the wall).
 */
export function snapRunToWallFace(model: PlanModel, cursor: Point, depth: number, tolerancePu: number): { point: Point; label: string } | null {
  let best: { point: Point; dist: number } | null = null;
  for (const w of model.walls.values()) {
    if (w.archivedAt !== null || w.kind === "boundary") continue;
    const a = nodeOf(model, w.startNodeId);
    const b = nodeOf(model, w.endNodeId);
    const ab = { x: b.x - a.x, y: b.y - a.y };
    const l2 = ab.x * ab.x + ab.y * ab.y;
    if (l2 === 0) continue;
    const t = Math.max(0, Math.min(1, ((cursor.x - a.x) * ab.x + (cursor.y - a.y) * ab.y) / l2));
    const px = a.x + ab.x * t;
    const py = a.y + ab.y * t;
    const dist = Math.hypot(cursor.x - px, cursor.y - py);
    if (dist <= tolerancePu + w.thickness / 2 + depth) {
      const n = leftNormal(a, b);
      const side = (cursor.x - px) * n.x + (cursor.y - py) * n.y >= 0 ? 1 : -1;
      const out = w.thickness / 2 + depth / 2;
      const p = roundPoint({ x: px + n.x * out * side, y: py + n.y * out * side });
      if (!best || dist < best.dist) best = { point: p, dist };
    }
  }
  return best ? { point: best.point, label: "⊥ Along wall" } : null;
}
